import apiClient from './apiClient';
import * as networkPrintApi from './networkPrintApi';

// Active label print destination, published by the currently-mounted
// PrintMethodToggle (per-screen). Lets the service route USB vs network.
const isNetworkActive = () => {
  try { return localStorage.getItem('lp_active_method') === 'network'; } catch (_) { return false; }
};
const getActiveNetPrinter = () => {
  try { return JSON.parse(localStorage.getItem('lp_active_printer') || 'null'); } catch (_) { return null; }
};
const getActiveOp = () => {
  try { return localStorage.getItem('lp_active_op') || null; } catch (_) { return null; }
};
export const isNetworkPrintActive = () => isNetworkActive();

// ==========================================
// Printer API Service
// ==========================================

// Get printer configuration
export const getPrinterConfig = async () => {
  const response = await apiClient.get('/printer/config');
  return response.data;
};

// Generate print commands for any codes
export const generatePrintCommands = async (codes, language = 'tspl', copies = 1, labelWidth = 33, labelHeight = 26, extraTexts = null) => {
  const body = {
    codes,
    language,
    copies,
    label_width_mm: labelWidth,
    label_height_mm: labelHeight
  };
  if (extraTexts) body.extra_texts = extraTexts;
  const response = await apiClient.post('/printer/generate-commands', body);
  return response.data;
};

// Generate print preview with QR images
export const generatePrintPreview = async (codes, labelWidth = 33, labelHeight = 26) => {
  const response = await apiClient.post('/printer/preview', {
    codes,
    label_width_mm: labelWidth,
    label_height_mm: labelHeight
  });
  return response.data;
};

// Get QR code image URL
export const getQRImageUrl = (code) => {
  const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
  return `${baseUrl}/api/printer/qr-image/${encodeURIComponent(code)}`;
};

// Get barcode image URL
export const getBarcodeImageUrl = (code) => {
  const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
  return `${baseUrl}/api/printer/barcode-image/${encodeURIComponent(code)}`;
};

// ==========================================
// USN Label Printing
// ==========================================

// Generate USN label commands
export const generateUSNLabels = async (usnCodes, language = 'tspl', copies = 1, sortNos = null, configId = null) => {
  const response = await apiClient.post('/printer/usn-labels', {
    usn_codes: usnCodes,
    language,
    copies,
    sort_nos: sortNos,
    config_id: configId
  });
  return response.data;
};

// Generate USN labels for a specific bundle
export const generateUSNLabelsForBundle = async (entryId, bundleBarcode, language = 'tspl', copies = 1) => {
  const response = await apiClient.post(
    `/printer/usn-labels/bundle/${entryId}`,
    null,
    { params: { bundle_barcode: bundleBarcode, language, copies } }
  );
  return response.data;
};

// ==========================================
// Bundle Label Printing
// ==========================================

// Generate bundle label commands
export const generateBundleLabels = async (bundleCodes, language = 'tspl', copies = 1) => {
  const response = await apiClient.post('/printer/bundle-labels', {
    bundle_codes: bundleCodes,
    language,
    copies
  });
  return response.data;
};

// ==========================================
// Document Label Printing
// ==========================================

// Generate document label commands
export const generateDocumentLabels = async (documentCodes, language = 'tspl', copies = 1) => {
  const response = await apiClient.post('/printer/document-labels', {
    document_codes: documentCodes,
    language,
    copies
  });
  return response.data;
};

// ==========================================
// Download Commands as File
// ==========================================

// Download print commands as file (for use with printer utilities)
export const downloadPrintCommands = async (codes, language = 'tspl', copies = 1, labelWidth = 33, labelHeight = 26) => {
  const response = await apiClient.post('/printer/download-commands', {
    codes,
    language,
    copies,
    label_width_mm: labelWidth,
    label_height_mm: labelHeight
  }, {
    responseType: 'blob'
  });
  
  // Create download link
  const extMap = { zpl: 'zpl', tspl: 'prn', escpos: 'bin' };
  const ext = extMap[language] || 'txt';
  const filename = `print_labels_${codes.length}.${ext}`;
  
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  
  return { success: true, filename };
};

// ==========================================
// USB Printer Communication (Web USB API)
// ==========================================

// Check if Web USB is supported
export const isWebUSBSupported = () => {
  return 'usb' in navigator;
};

// Request USB printer access
export const requestUSBPrinter = async () => {
  // Network destination active for the current screen — no USB device needed.
  if (isNetworkActive()) {
    return { __network: true, netPrinter: getActiveNetPrinter(), productName: 'Network Printer' };
  }
  if (!isWebUSBSupported()) {
    throw new Error('Web USB is not supported in this browser');
  }
  
  try {
    // Common thermal printer vendor IDs
    const filters = [
      { vendorId: 0x1203 }, // TSC
      { vendorId: 0x0A5F }, // Zebra
      { vendorId: 0x04B8 }, // Epson
      { vendorId: 0x0416 }, // Winbond (some thermal printers)
    ];
    
    const device = await navigator.usb.requestDevice({ filters });
    return device;
  } catch (error) {
    if (error.name === 'NotFoundError') {
      throw new Error('No USB printer found. Please connect a thermal printer.');
    }
    throw error;
  }
};

// Send data to USB printer (or route to the network Print Bridge when the
// current screen's destination switch is set to "network").
export const sendToUSBPrinter = async (device, commands, options = {}) => {
  const { clientToken = null } = options;
  if (device?.__network || isNetworkActive()) {
    const sel = device?.netPrinter || getActiveNetPrinter();
    const operationKey = getActiveOp();
    // With a central operation→station route, we can print even without a
    // local pick. Only block if there's neither a local selection nor a routed
    // operation to resolve against.
    if (!sel && !operationKey) {
      throw new Error('No network printer selected. Choose one from the USB / Network switch.');
    }
    const res = await networkPrintApi.printViaAgent({
      agentId: sel?.agentId, printerName: sel?.printerName, payload: commands, labelType: 'label',
      operationKey, clientToken,
    });
    if (!res.success) throw new Error(res.error || 'Network print failed');
    return { success: true };
  }
  try {
    await device.open();
    
    // Select configuration and claim interface
    if (device.configuration === null) {
      await device.selectConfiguration(1);
    }
    await device.claimInterface(0);
    
    // Find the OUT endpoint
    const endpoint = device.configuration.interfaces[0].alternate.endpoints.find(
      e => e.direction === 'out'
    );
    
    if (!endpoint) {
      throw new Error('No OUT endpoint found on printer');
    }
    
    // Convert commands to Uint8Array
    const data = new TextEncoder().encode(commands);
    
    // Send data
    await device.transferOut(endpoint.endpointNumber, data);
    
    // Release and close
    await device.releaseInterface(0);
    await device.close();
    
    return { success: true };
  } catch (error) {
    // Try to close device on error
    try {
      await device.close();
    } catch (closeError) {
      console.warn('Error closing device:', closeError);
    }
    
    // Provide helpful error messages for common issues
    if (error.message?.includes('Access denied')) {
      throw new Error(
        'USB Access Denied. Please try:\n' +
        '1. Close any printer software (BarTender, TSC utility, etc.)\n' +
        '2. Unplug and replug the USB cable\n' +
        '3. Use "Download File" method instead'
      );
    }
    
    if (error.message?.includes('Unable to claim interface')) {
      throw new Error(
        'Printer is in use by another program. Please close other printer applications and try again.'
      );
    }
    
    throw error;
  }
};

// Print labels via Web USB
export const printViaUSB = async (codes, language = 'tspl', copies = 1) => {
  // First generate the commands
  const result = await generatePrintCommands(codes, language, copies);
  
  // Request printer access
  const device = await requestUSBPrinter();
  
  // Send to printer
  await sendToUSBPrinter(device, result.commands);
  
  return {
    success: true,
    codeCount: codes.length,
    copiesPerLabel: copies
  };
};
